import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, Upload, X, ArrowLeft, Loader2, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "credit_card_fee", label: "💳 Credit Card Fee", icon: "💳" },
  { value: "bank_fee",        label: "🏦 Bank Fee",        icon: "🏦" },
  { value: "software",        label: "💻 Software",        icon: "💻" },
  { value: "development",     label: "🛠 Development",     icon: "🛠" },
  { value: "travel",          label: "✈️ Business Travel", icon: "✈️" },
  { value: "visa_cost",       label: "📋 Visa Cost",       icon: "📋" },
  { value: "marketing",       label: "📣 Marketing",       icon: "📣" },
  { value: "office",          label: "🏢 Office / Admin",  icon: "🏢" },
  { value: "meal",            label: "🍽️ Business Meal",   icon: "🍽️" },
  { value: "other",           label: "📦 Other",           icon: "📦" },
];

const ACCOUNTS = ["AMEX B", "Revolut Business", "Cash"];

function autoQuarter(dateStr) {
  const m = new Date(dateStr).getMonth();
  return `Q${Math.floor(m / 3) + 1}`;
}

export default function QuickExpense() {
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const [step, setStep] = useState("form"); // form | success
  const [saving, setSaving] = useState(false);
  const [receipt, setReceipt] = useState(null); // { filename, url, data, mime }
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    date: today,
    description: "",
    amount: "",
    category: "meal",
    payment_account: "AMEX B",
    vat_type: "Outside Scope EU VAT",
    vat_amount: 0,
    notes: "",
  });

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      try {
        // Try R2 upload — keeps MongoDB lean
        const res = await axios.post(`${API}/uploads/receipt`, {
          file_data: base64,
          filename: file.name,
          mime_type: file.type,
          folder: "expense-receipts",
          invoice_number: "",
        }, { headers: getAuthHeaders() });

        if (res.data.url) {
          setReceipt({ filename: file.name, url: res.data.url, data: null, mime: file.type });
          toast.success("Receipt uploaded ✓");
        } else {
          // R2 not configured — fall back to base64
          setReceipt({ filename: file.name, url: null, data: base64, mime: file.type });
        }
      } catch (err) {
        // Fallback
        setReceipt({ filename: file.name, url: null, data: base64, mime: file.type });
      }
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.description) { toast.error("Add a description"); return; }
    if (!form.amount || isNaN(parseFloat(form.amount))) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/accounting/expenses`, {
        ...form,
        amount: parseFloat(form.amount),
        vat_amount: parseFloat(form.vat_amount) || 0,
        quarter: autoQuarter(form.date),
        receipt_url: receipt?.url || null,
        receipt_data: receipt?.data || null,     // only set if R2 not available
        receipt_filename: receipt?.filename || null,
        receipt_mime_type: receipt?.mime || null,
      }, { headers: getAuthHeaders() });
      setStep("success");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setForm({ date: today, description: "", amount: "", category: "meal", payment_account: "AMEX B", vat_type: "Outside Scope EU VAT", vat_amount: 0, notes: "" });
    setReceipt(null);
    setStep("form");
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-purple-600 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-8 text-center max-w-xs w-full shadow-xl">
          <div className="text-4xl mb-4">🔒</div>
          <p className="text-gray-600 text-sm">Please log in first at</p>
          <a href="/login" className="text-purple-600 font-bold block mt-2">app.thebetterfare.com/login</a>
        </div>
      </div>
    );
  }

  if (step === "success") {
    return (
      <div className="min-h-screen bg-purple-600 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-8 text-center max-w-xs w-full shadow-xl">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-1">Saved!</h2>
          <p className="text-gray-500 text-sm mb-2">
            €{parseFloat(form.amount).toFixed(2)} — {form.description}
          </p>
          <p className="text-xs text-gray-400 mb-6">{autoQuarter(form.date)} · {form.category}{receipt ? " · 📎 receipt" : ""}</p>
          <button onClick={reset}
            className="w-full bg-purple-600 text-white font-semibold py-3 rounded-xl text-base active:bg-purple-700">
            Add Another
          </button>
          <a href="/accounting" className="block mt-3 text-sm text-purple-600 hover:underline">
            View All Expenses →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-purple-600">
      <div className="px-5 pt-12 pb-6">
        <a href="/accounting" className="flex items-center gap-1 text-purple-200 text-sm mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </a>
        <h1 className="text-white text-2xl font-bold">Quick Expense</h1>
        <p className="text-purple-200 text-sm mt-1">The Better Fare</p>
      </div>

      <div className="bg-white rounded-t-3xl min-h-screen px-5 pt-6 pb-10 space-y-5">

        {/* Amount */}
        <div className="text-center py-4">
          <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Amount (EUR)</p>
          <div className="flex items-center justify-center gap-1">
            <span className="text-4xl font-light text-gray-400">€</span>
            <input
              type="number" inputMode="decimal" step="0.01"
              value={form.amount}
              onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
              placeholder="0.00"
              className="text-5xl font-bold text-gray-900 w-48 text-center border-0 outline-none bg-transparent"
              autoFocus
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Description</label>
          <input
            value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            placeholder="e.g. Business dinner with client"
            className="w-full mt-1.5 px-4 py-3 rounded-xl border border-gray-200 text-base outline-none focus:border-purple-400"
          />
        </div>

        {/* Category */}
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Category</label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {CATEGORIES.map(c => (
              <button key={c.value} onClick={() => setForm(p => ({ ...p, category: c.value }))}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                  form.category === c.value
                    ? "bg-purple-600 border-purple-600 text-white"
                    : "bg-gray-50 border-gray-200 text-gray-700 active:bg-gray-100"
                }`}>
                <span className="text-base">{c.icon}</span>
                <span className="truncate">{c.label.split(" ").slice(1).join(" ")}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Date & Account */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Date</label>
            <input type="date" value={form.date}
              onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
              className="w-full mt-1.5 px-3 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-purple-400" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Paid with</label>
            <select value={form.payment_account}
              onChange={e => setForm(p => ({ ...p, payment_account: e.target.value }))}
              className="w-full mt-1.5 px-3 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-purple-400 bg-white">
              {ACCOUNTS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>

        {/* VAT */}
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">VAT (if applicable)</label>
          <div className="grid grid-cols-2 gap-3 mt-1.5">
            <select value={form.vat_type}
              onChange={e => setForm(p => ({ ...p, vat_type: e.target.value }))}
              className="px-3 py-3 rounded-xl border border-gray-200 text-sm outline-none bg-white">
              <option value="Outside Scope EU VAT">Outside Scope</option>
              <option value="0% VAT">0% VAT</option>
              <option value="21% VAT">21% VAT</option>
              <option value="9% VAT">9% VAT</option>
            </select>
            <input type="number" step="0.01" placeholder="VAT €0.00"
              value={form.vat_amount || ""}
              onChange={e => setForm(p => ({ ...p, vat_amount: parseFloat(e.target.value) || 0 }))}
              className="px-3 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-purple-400" />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Notes (optional)</label>
          <input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
            placeholder="Any additional notes"
            className="w-full mt-1.5 px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-purple-400" />
        </div>

        {/* Receipt — no capture="environment" so gallery + camera both work */}
        <div>
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Receipt</label>
          <div className="mt-1.5">
            {receipt ? (
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl border border-green-200">
                <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                <span className="text-sm text-green-700 truncate flex-1">{receipt.filename}</span>
                <div className="flex items-center gap-1 shrink-0">
                  {receipt.url && (
                    <a href={receipt.url} target="_blank" rel="noopener noreferrer" className="text-blue-500">
                      <Eye className="w-4 h-4" />
                    </a>
                  )}
                  <button onClick={() => setReceipt(null)} className="text-red-400">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-gray-200 rounded-xl p-5 text-center active:bg-gray-50">
                <Upload className="w-7 h-7 mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-400">
                  {uploading ? "Uploading to cloud..." : "Take photo or upload from gallery"}
                </p>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*,application/pdf"
              className="hidden" onChange={handleFile} />
          </div>
        </div>

        <button onClick={handleSave} disabled={saving}
          className="w-full bg-purple-600 text-white font-bold py-4 rounded-2xl text-lg active:bg-purple-700 disabled:opacity-60 flex items-center justify-center gap-2 mt-2">
          {saving ? <><Loader2 className="w-5 h-5 animate-spin" /> Saving...</> : "Save Expense"}
        </button>
      </div>
    </div>
  );
}
